import { useInfiniteQuery } from "@tanstack/react-query";
import { termsAndConditionsService } from "../api/services/termsAndConditionsService";
import type {
  TermsAndConditions,
  TermsAndConditionsFilters,
  TermsAndConditionsResult,
} from "../schemas/termsAndConditionsSchema";
import { useTermsAndConditions } from "./useTermsAndConditions";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type TermsAndConditionsInfiniteFilters = Omit<
  TermsAndConditionsFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useTermsAndConditionsInfinite = (
  filters: TermsAndConditionsInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useTermsAndConditions(undefined, { enabled: false });

  const query = useInfiniteQuery<TermsAndConditionsResult, Error>({
    queryKey: ["termsAndConditions", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      termsAndConditionsService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const termsAndConditions: TermsAndConditions[] =
    query.data?.pages.flatMap((p) => p.termsAndConditions) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    termsAndConditions,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
